/**
 * Data model and pure helpers of the condition builder: nested AND/OR groups
 * of rules. Every change returns a new tree (the builder never mutates the
 * value it was given), so a caller's `v-model` sees plain replacements.
 */

export type NxConditionCombinator = 'and' | 'or';

export interface NxConditionRule {
  id: string;
  field: string;
  op: string;
  value: unknown;
}

export interface NxConditionGroup {
  id: string;
  combinator: NxConditionCombinator;
  children: NxConditionNode[];
}

export type NxConditionNode = NxConditionGroup | NxConditionRule;

/** A field the rules can test. `group` heads it in the field dropdown. */
export interface NxConditionField {
  key: string;
  label: string;
  /** Free hint for the caller's value editor (`'number'`, `'date'`, …). */
  type?: string;
  group?: string;
}

/**
 * An operator of a field. `arity` says how many values it takes: `0` none
 * („ist leer“), `1` one, `2` a range, `'many'` a list.
 */
export interface NxConditionOperator {
  key: string;
  label: string;
  arity: 0 | 1 | 2 | 'many';
}

export type NxConditionOperators = (field: NxConditionField | undefined) => NxConditionOperator[];

let seq = 0;
/** A fresh id for a node. */
export function conditionId(): string {
  seq += 1;
  return `c${Date.now().toString(36)}${seq.toString(36)}`;
}

export function isConditionGroup(node: NxConditionNode): node is NxConditionGroup {
  return (node as NxConditionGroup).children !== undefined;
}

export function createConditionRule(field = '', op = '', value: unknown = ''): NxConditionRule {
  return { id: conditionId(), field, op, value };
}

export function createConditionGroup(
  combinator: NxConditionCombinator = 'and',
  children: NxConditionNode[] = []
): NxConditionGroup {
  return { id: conditionId(), combinator, children };
}

/** Deep copy with new ids — for „duplicate“. */
export function cloneConditionNode<T extends NxConditionNode>(node: T): T {
  if (isConditionGroup(node)) {
    return {
      ...node,
      id: conditionId(),
      children: node.children.map((child) => cloneConditionNode(child)),
    } as T;
  }
  return { ...node, id: conditionId() } as T;
}

/** Number of rules in the tree (groups not counted). */
export function countConditionRules(node: NxConditionNode): number {
  return isConditionGroup(node)
    ? node.children.reduce((n, child) => n + countConditionRules(child), 0)
    : 1;
}

/** The value a rule starts with for an operator of that arity. */
export function emptyValueFor(arity: NxConditionOperator['arity'] | undefined): unknown {
  if (arity === 0) return null;
  if (arity === 2) return ['', ''];
  if (arity === 'many') return [];
  return '';
}

export interface DescribeConditionOptions {
  fields: readonly NxConditionField[];
  operators: NxConditionOperators;
  /** Formats a rule's value. Defaults to the text of the value (lists joined by „, “, ranges by „–“). */
  formatValue?: (value: unknown, rule: NxConditionRule) => string;
  andLabel?: string;
  orLabel?: string;
}

function defaultFormat(value: unknown, arity: NxConditionOperator['arity'] | undefined): string {
  if (arity === 0) return '';
  if (Array.isArray(value)) {
    const parts = value.map((v) => String(v ?? '').trim()).filter(Boolean);
    return arity === 2 ? parts.join('–') : parts.join(', ');
  }
  return value == null ? '' : String(value).trim();
}

/**
 * The tree as one line of text: „Status ist Offen UND (Betrag > 100 ODER
 * Kunde enthält Weber)“. Incomplete rules (no field or operator) are left out;
 * a nested group with more than one part is bracketed.
 */
export function describeCondition(
  group: NxConditionGroup,
  options: DescribeConditionOptions
): string {
  const and = options.andLabel ?? 'UND';
  const or = options.orLabel ?? 'ODER';

  function rule(r: NxConditionRule): string {
    const field = options.fields.find((f) => f.key === r.field);
    if (!field || !r.op) return '';
    const op = options.operators(field).find((o) => o.key === r.op);
    if (!op) return '';
    const value = options.formatValue
      ? options.formatValue(r.value, r)
      : defaultFormat(r.value, op.arity);
    return [field.label, op.label, value].filter(Boolean).join(' ');
  }

  function node(n: NxConditionNode, nested: boolean): string {
    if (!isConditionGroup(n)) return rule(n);
    const parts = n.children.map((child) => node(child, true)).filter(Boolean);
    const text = parts.join(` ${n.combinator === 'and' ? and : or} `);
    return nested && parts.length > 1 ? `(${text})` : text;
  }

  return node(group, false);
}
